import { Box, Card, CardContent, Grid, Typography } from '@material-ui/core';
import React, { ReactElement, useEffect, useState } from 'react';
import { TypographyEllipsis } from '../../../components/output/TypographyEllipsis';
import { Game } from '../../../api/games/model';
import { toErrorMessage } from '../../../api/error/error';
import { FinishGameDialog } from '../game/gameresult/FinishGameDialog';
import { useGamesState } from '../../../api/games/useGames';
import { DataContainer } from '../../../components/layout/DataContainer';
import { formatDateTime } from '../../../util/date';
import { GameDateAndLocationDialog } from './GameDateAndLocationDialog';
import PeopleIcon from '@material-ui/icons/People';

type ChallengesListProps = {
  readonlyOpenGames?: Game[];
};
export const ChallengesList = ({ readonlyOpenGames }: ChallengesListProps) => {
  const { openGames: loadedOpenGames, isLoading, isSuccess, isError, error } = useGamesState();
  const [openGames, setOpenGames] = useState(undefined);
  const readonly = !!readonlyOpenGames;

  useEffect(() => {
    if (readonlyOpenGames) {
      setOpenGames(readonlyOpenGames);
      return;
    }
    if (loadedOpenGames) {
      setOpenGames(loadedOpenGames);
    }
  }, [loadedOpenGames, readonlyOpenGames]);

  const defaultEmptyMessage = 'Keine offenen Forderungen vorhanden.';
  const determineEmptyMessage = () => {
    if (isLoading) return 'Forderungen werden geladen';
    if (isError) return `Fehler beim Laden.\n${toErrorMessage(error)}`;
    return defaultEmptyMessage;
  };
  return (
    <DataContainer
      isLoading={readonlyOpenGames ? false : isLoading}
      isSuccess={readonlyOpenGames ? true : isSuccess}
      isEmpty={!openGames || openGames.length === 0}
      emptyMessage={readonlyOpenGames ? defaultEmptyMessage : determineEmptyMessage()}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box display="flex" flexDirection="column" style={{ gap: '12px' }}>
            {openGames?.map((game) => <ChallengeEntry key={game.id} openGame={game} readonly={readonly} />)}
          </Box>
        </Grid>
      </Grid>
    </DataContainer>
  );
};

interface ChallengeEntryProps {
  openGame: Game;
  readonly: boolean;
}

export const ChallengeEntry: React.FC<ChallengeEntryProps> = ({ openGame, readonly }) => {
  return (
    <Box>
      <Card elevation={1} style={{ background: '#f5f5f5' }}>
        <CardContent>
          <Grid container spacing={1}>
            <Grid item xs={4} container justifyContent="flex-start" alignItems="center"></Grid>
            <Grid item xs={8} container justifyContent="flex-end" alignItems="center" style={{ gap: '8px' }}>
              <Typography variant="body2" color="textSecondary" align="right">
                {openGame.challengeTimestamp.toLocaleString()}
              </Typography>
              {!readonly && <GameDateAndLocationDialog gameId={openGame.id} gameDetails={openGame.gameDetails} />}
            </Grid>
            <Grid item xs={12} style={{ paddingTop: '12px' }}>
              <Grid container spacing={0}>
                <Grid item xs={12}>
                  <Box display={'flex'} alignItems={'flex-end'}>
                    <TypographyEllipsis variant="body1">{openGame.challenger.name}</TypographyEllipsis>
                    {openGame.challenger.ranking !== 0 && (
                      <Typography>&nbsp;({openGame.challenger.ranking})</Typography>
                    )}
                    <Typography variant={'caption'} color={'textSecondary'}>
                      &nbsp;{'fordert'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box display={'flex'} alignItems={'flex-end'}>
                    <TypographyEllipsis variant="body1">{openGame.defender.name}</TypographyEllipsis>
                    {openGame.defender.ranking !== 0 && <Typography>&nbsp;({openGame.defender.ranking})</Typography>}
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            <>
              <Grid item xs={12} container style={openGame.gameDetails && { paddingBottom: '16px' }}>
                {openGame.gameDetails.expectedStartTimestamp && (
                  <GameDetailEntry
                    label={'Startzeit:'}
                    value={formatDateTime(openGame.gameDetails.expectedStartTimestamp)}
                  />
                )}
                {openGame.gameDetails.location && (
                  <GameDetailEntry label={'Spielort:'} value={openGame.gameDetails.location} />
                )}
                {openGame.gameDetails.spectatorsAllowed && (
                  <Grid item xs={12} style={{ maxHeight: '18px', marginTop: '12px' }} container alignItems="center">
                    <PeopleIcon style={{ maxHeight: '18px' }} />
                    <Typography component="span" variant={'caption'} color={'textPrimary'} style={{ lineHeight: 1 }}>
                      Gäste sind erwünscht.
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </>
            {!readonly && (
              <Grid item xs={12} container alignContent={'flex-end'} justifyContent={'center'}>
                <FinishGameDialog gameId={openGame.id} challenger={openGame.challenger} defender={openGame.defender} />
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

type GameDetailEntryProps = {
  label: string;
  value: string;
};

export const GameDetailEntry = ({ label, value }: GameDetailEntryProps): ReactElement => {
  return (
    <>
      <Grid item xs={3} style={{ maxHeight: '18px' }}>
        <Typography component="span" variant={'caption'} color={'textSecondary'} style={{ lineHeight: 1 }}>
          {label}
        </Typography>
      </Grid>
      <Grid item xs={9} style={{ maxHeight: '18px' }}>
        <Typography component="span" variant={'caption'} color={'textPrimary'} style={{ lineHeight: 1 }}>
          {value}
        </Typography>
      </Grid>
    </>
  );
};
